import { useState, memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { alpha, useTheme, experimentalStyled as styled } from '@material-ui/core/styles';
import { Box, List, ListItem, Collapse, ListItemText, ListItemIcon, ListSubheader } from '@material-ui/core';
import { PATH_DASHBOARD } from '../routes/paths';
import { prefetchTicketsPageFromSession } from '../pages/tickets/supportTickets/hooks/ticketsListCache';

// ----------------------------------------------------------------------

const ListSubheaderStyle = styled((props) => <ListSubheader disableSticky disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.overline,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(5),
    color: theme.palette.text.primary
  })
);

const ListItemStyle = styled((props) => <ListItem button disableGutters {...props} />)(({ theme, isCollapsed }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  paddingLeft: isCollapsed ? theme.spacing(2) : theme.spacing(5),
  paddingRight: isCollapsed ? theme.spacing(1) : theme.spacing(2.5),
  color: theme.palette.text.secondary,
  justifyContent: isCollapsed ? 'center' : 'flex-start',
  minWidth: isCollapsed ? 48 : 'auto',
  display: 'flex',
  alignItems: 'center',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
  marginBottom: isCollapsed ? theme.spacing(0.5) : 0,
  borderRadius: isCollapsed ? theme.spacing(1) : 0,
  overflow: 'hidden',
  isolation: 'isolate',
  '&:hover': {
    backgroundColor: isCollapsed ? 'action.hover' : 'action.hover',
    borderRadius: isCollapsed ? theme.spacing(1) : 0,
    transform: isCollapsed ? 'scale(1.02)' : 'none',
    transition: 'all 0.2s ease-in-out'
  },
  '&:before': {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: 'none',
    position: 'absolute',
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main
  }
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

// ----------------------------------------------------------------------

const NavItem = memo(({ item, active, isCollapsed }) => {
  const theme = useTheme();
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children } = item;
  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const activeRootStyle = useMemo(() => ({
    color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    '&:before': { display: 'block' }
  }), [theme.palette.primary.main, theme.palette.action.selectedOpacity]);

  const activeSubStyle = useMemo(() => ({
    color: 'text.primary',
    fontWeight: 'fontWeightMedium'
  }), []);

  if (children) {
    const listItem = (
      <ListItemStyle
        onClick={handleOpen}
        isCollapsed={isCollapsed}
        sx={{
          ...(isActiveRoot && activeRootStyle),
          cursor: 'pointer'
        }}
      >
        <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
        {!isCollapsed && (
          <>
            <ListItemText disableTypography primary={title} />
            {info && info}
            <Box
              component={Icon}
              icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
              sx={{ width: 16, height: 16, ml: 1 }}
            />
          </>
        )}
      </ListItemStyle>
    );

    return (
      <>
        {isCollapsed ? (
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {listItem}
          </Box>
        ) : (
          listItem
        )}

        {!isCollapsed && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children?.map((item) => {
                const { title, path } = item;
                const isActiveSub = active(path);

                return (
                  <ListItemStyle
                    key={title}
                    component={RouterLink}
                    to={path}
                    isCollapsed={isCollapsed}
                    sx={{
                      ...(isActiveSub && activeSubStyle)
                    }}
                  >
                    <ListItemIconStyle>
                      <Box
                        component="span"
                        sx={{
                          width: 4,
                          height: 4,
                          display: 'flex',
                          borderRadius: '50%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'text.disabled',
                          transition: (theme) => theme.transitions.create('transform'),
                          ...(isActiveSub && {
                            transform: 'scale(2)',
                            bgcolor: 'primary.main'
                          })
                        }}
                      />
                    </ListItemIconStyle>
                    <ListItemText disableTypography primary={title} />
                  </ListItemStyle>
                );
              })}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  const handlePrefetch = useCallback(() => {
    if (path === PATH_DASHBOARD.tickets.root) {
      prefetchTicketsPageFromSession();
    }
  }, [path]);

  const listItem = (
    <ListItemStyle
      component={RouterLink}
      to={path}
      isCollapsed={isCollapsed}
      onMouseEnter={handlePrefetch}
      onFocus={handlePrefetch}
      sx={{
        ...(isActiveRoot && activeRootStyle),
        cursor: 'pointer'
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      {!isCollapsed && (
        <>
          <ListItemText disableTypography primary={title} />
          {info && info}
        </>
      )}
    </ListItemStyle>
  );

  return isCollapsed ? (
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      {listItem}
    </Box>
  ) : (
    listItem
  );
});

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
  isCollapsed: PropTypes.bool
};

const NavSection = memo(({ navConfig, isCollapsed, ...other }) => {
  const { pathname } = useLocation();
  
  const match = useCallback((path) => {
    if (!path) return false;
    if (path === '/dashboard/ticket') {
      if (pathname.includes('/ticket/view/status-logs')) {
        return false;
      }
    }
    return !!matchPath({ path, end: false }, pathname);
  }, [pathname]);

  const renderedItems = useMemo(() => {
    return navConfig?.map((list) => {
      const { subheader, items } = list;
      return (
        <List key={subheader} disablePadding>
          {/* <ListSubheaderStyle>{subheader}</ListSubheaderStyle> */}
          {items?.map((item) => (
            <NavItem key={item.title} item={item} active={match} isCollapsed={isCollapsed} />
          ))}
        </List>
      );
    });
  }, [navConfig, match, isCollapsed]);

  return (
    <Box {...other}>
      {renderedItems}
    </Box>
  );
});

NavSection.propTypes = {
  navConfig: PropTypes.array,
  isCollapsed: PropTypes.bool
};

NavSection.displayName = 'NavSection';

export default NavSection;
